import React, { useState, useCallback, useRef } from 'react';
import { fetchUserBadgeAwards } from '../../api';
import './MoodleBadgeFlags.css';

/**
 * Normalisiert eine E-Mail-Adresse, indem Timestamps oder zusätzliche Suffixe entfernt werden
 * Beispiel: "name@domain.com.1753678822" -> "name@domain.com"
 */
const normalizeEmail = (email) => {
  if (!email) return '';
  
  // Entferne alles nach dem zweiten Punkt (nach der TLD)
  // Beispiel: "name@domain.com.timestamp" -> "name@domain.com"
  const parts = email.split('.');
  if (parts.length > 2) {
    // Finde den letzten Teil vor dem Timestamp (sollte die TLD sein)
    const lastValidPartIndex = parts.findIndex((part, index) => 
      index >= 2 && /^\d+$/.test(part) // Timestamp ist nur Zahlen
    );
    
    if (lastValidPartIndex > 0) {
      return parts.slice(0, lastValidPartIndex).join('.');
    }
  }
  
  return email;
};

/**
 * Prüft, ob zwei E-Mail-Adressen ähnlich sind (flexibler Vergleich)
 */
const isEmailSimilar = (email1, email2) => {
  if (!email1 || !email2) return false;
  
  // Direkte Übereinstimmung
  if (email1 === email2) return true;
  
  // Extrahiere den lokalen Teil (vor @) für flexiblen Vergleich
  const getLocalPart = (email) => {
    const atIndex = email.indexOf('@');
    return atIndex > 0 ? email.substring(0, atIndex) : email;
  };
  
  const local1 = getLocalPart(email1).toLowerCase();
  const local2 = getLocalPart(email2).toLowerCase();
  
  // Vergleiche lokale Teile (vor @)
  if (local1 === local2) return true;
  
  // Entferne Punkte und Unterstriche für flexibleren Vergleich
  const normalize = (str) => str.replace(/[._]/g, '');
  const normalized1 = normalize(local1);
  const normalized2 = normalize(local2);
  
  return normalized1 === normalized2;
};

/**
 * Findet einen Moodle-Benutzer basierend auf Student-Daten (E-Mail, Login, Username)
 */
const findMoodleUser = (userBadgeData, student) => {
  if (!student || !userBadgeData) return null;
  
  const { email: studentEmail, login: studentLogin, username: studentUsername } = student;
  
  // 1. Wenn E-Mail vorhanden ist, verwende E-Mail-basiertes Matching
  if (studentEmail) {
    const normalizedStudentEmail = normalizeEmail(studentEmail);
    
    // 1a. Versuche exakte Übereinstimmung mit normalisierten E-Mails
    let user = userBadgeData.find(u => {
      const normalizedUserEmail = normalizeEmail(u.email);
      const normalizedUserUsername = normalizeEmail(u.username);
      
      return normalizedUserEmail === normalizedStudentEmail || 
             normalizedUserUsername === normalizedStudentEmail;
    });
    
    if (user) return { user, matchType: 'exact-email' };
    
    // 1b. Versuche ähnlichkeitsbasierten E-Mail-Vergleich
    user = userBadgeData.find(u => {
      const normalizedUserEmail = normalizeEmail(u.email);
      const normalizedUserUsername = normalizeEmail(u.username);
      
      return isEmailSimilar(normalizedUserEmail, normalizedStudentEmail) || 
             isEmailSimilar(normalizedUserUsername, normalizedStudentEmail);
    });
    
    if (user) return { user, matchType: 'similar-email' };
  }
  
  // 2. Wenn keine E-Mail vorhanden oder kein E-Mail-Match, verwende Login/Username-Matching
  if (studentLogin || studentUsername) {
    const user = userBadgeData.find(u => {
      // Vergleiche Login und Username direkt (case-insensitive)
      const usernameLower = u.username ? u.username.toLowerCase() : '';
      const studentLoginLower = studentLogin ? studentLogin.toLowerCase() : '';
      const studentUsernameLower = studentUsername ? studentUsername.toLowerCase() : '';
      
      return (studentLoginLower && usernameLower === studentLoginLower) ||
             (studentUsernameLower && usernameLower === studentUsernameLower);
    });
    
    if (user) return { user, matchType: 'username-login' };
  }
  
  return null;
};

/**
 * Bestimmt die Farbe für ein Badge basierend auf der Badge-ID
 */
const getBadgeColor = (badgeId) => {
  const colors = [
    '#4caf50', // Grün
    '#2196f3', // Blau
    '#ff9800', // Orange
    '#9c27b0', // Lila
    '#f44336', // Rot
    '#00bcd4', // Cyan
    '#795548', // Braun
    '#607d8b'  // Blaugrau
  ];
  
  return colors[badgeId % colors.length];
};

/**
 * Holt die konfigurierte Farbe für ein Badge aus den Task-Daten
 */
const getBadgeColorFromTaskData = (badgeId, taskBadges) => {
  if (!taskBadges || !Array.isArray(taskBadges)) return getBadgeColor(badgeId);
  
  const configuredBadge = taskBadges.find(badge => 
    badge.id === badgeId || badge.id === badgeId.toString()
  );
  
  return configuredBadge?.flagColor || getBadgeColor(badgeId);
};

/**
 * Berechnet echte Flag-Positionen basierend auf Badge-Zuweisungsdaten aus Moodle
 * Der Badge-Container ist 100px breiter als die Tabelle und beginnt 50px links davon
 */
const calculateRealFlagPositions = (userBadgeData, student, badgeIds, startDate, endDate, taskData, tableWidth = 300) => {
  if (!userBadgeData || !badgeIds || badgeIds.length === 0) return [];
  
  // Stelle sicher, dass Daten Date-Objekte sind
  const taskStartDate = startDate instanceof Date ? startDate : new Date(startDate);
  const taskEndDate = endDate instanceof Date ? endDate : new Date(endDate);
  
  if (isNaN(taskStartDate.getTime()) || isNaN(taskEndDate.getTime())) {
    return [];
  }
  
  // === IDENTISCHE LOGIK WIE MovableBar calculateBarPosition ===
  // Die Container-Breite entspricht der aktuellen Ansicht
  const visibleWeeks = taskData?.visibleWeeks || 10;
  const weekWidth = taskData?.weekWidth || 200;
  const containerWidthPixels = visibleWeeks * weekWidth;
  
  // Verwende das viewStartDate aus dem StartDate-Context (identisch zu MovableBar)
  const viewStartDate = taskData?.viewStartDate ? new Date(taskData.viewStartDate) : new Date();
  const dayOfWeek = viewStartDate.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  viewStartDate.setDate(viewStartDate.getDate() + mondayOffset);
  viewStartDate.setHours(0, 0, 0, 0);
  
  // Berechne Millisekunden pro Pixel (identisch zu MovableBar)
  const msPerWeek = 7 * 24 * 60 * 60 * 1000; // Millisekunden pro Woche
  const pixelsPerMs = weekWidth / msPerWeek;
  
  const flags = [];
  

  
  // Finde den Moodle-Benutzer mit verbesserter Logik
  const matchResult = findMoodleUser(userBadgeData, student);
  
  if (matchResult) {
    // eslint-disable-next-line no-unused-vars
    const { user, matchType } = matchResult;
    // Filtere nur die Badges, die in der Task konfiguriert sind
    const relevantBadges = user.badges.filter(badge => 
      badgeIds.includes(badge.badgeId) || badgeIds.includes(badge.badgeId.toString())
    );
    

    
    relevantBadges.forEach((badge, index) => {
      // Stelle sicher, dass issuedDate ein Date-Objekt ist
      const issuedDate = badge.issuedDate instanceof Date 
        ? badge.issuedDate 
        : new Date(badge.issuedDate);
      
      // Prüfe, ob das Datum gültig ist
      if (isNaN(issuedDate.getTime())) {
        return; // Überspringe dieses Badge
      }
      
      // === ABSOLUTE POSITIONIERUNG RELATIV ZUR MovableBar ===
      // Berechne Badge-Position in Pixeln relativ zum Wochen-Raster (identisch zu MovableBar)
      const badgeOffsetMs = issuedDate.getTime() - viewStartDate.getTime();
      const badgePositionPx = badgeOffsetMs * pixelsPerMs;
      
      // Berechne MovableBar-Position in Pixeln
      const taskXPercent = taskData?.taskXPosition || 0;
      // eslint-disable-next-line no-unused-vars
      const taskStartPx = (taskXPercent / 100) * containerWidthPixels;
      
      // Badge-Position relativ zur MovableBar-Start-Position
      const relativeTaskStartOffsetMs = taskStartDate.getTime() - viewStartDate.getTime();
      const taskStartPositionPx = relativeTaskStartOffsetMs * pixelsPerMs;
      
      // Badge-Position relativ zum MovableBar-Start (nicht zum Container-Start)
      const badgePositionRelativeToTask = badgePositionPx - taskStartPositionPx;
      
      
      
      flags.push({
        id: `${student.id}-${badge.badgeId}-real`,
        badgeId: badge.badgeId,
        badgeName: badge.badgeName,
        color: getBadgeColorFromTaskData(badge.badgeId, taskData?.evidenceData?.badges),
        position: badgePositionRelativeToTask - 5, // Position relativ zur MovableBar-Start-Position mit 5px Versatz nach links
        date: issuedDate,
        type: 'moodle-badge',
        studentId: student.id,
        imageUrl: badge.imageUrl,
        description: `Badge "${badge.badgeName}" erhalten am ${issuedDate.toLocaleDateString('de-DE')}`,
        isReal: true,
        expireDate: badge.expireDate ? (badge.expireDate instanceof Date ? badge.expireDate : new Date(badge.expireDate)) : null,
        isWithinTaskPeriod: issuedDate >= taskStartDate && issuedDate <= taskEndDate // Basiert auf Task-Zeitraum für Styling
      });
    });
    
  } else {
    // Fallback wenn kein passender Moodle-User gefunden wird
    if (userBadgeData.length > 0) {
      const studentIndex = (parseInt(student.id) || 0) % userBadgeData.length;
      // eslint-disable-next-line no-unused-vars
      const user = userBadgeData[studentIndex];
    }
  }
  

  return flags;
};

/**
 * Hilfsfunktion zur Erkennung von überlappenden Badges
 */
const findOverlappingBadges = (flags, threshold = 30) => {
  const overlappingGroups = [];
  const processed = new Set();
  
  flags.forEach((flag, index) => {
    if (processed.has(index)) return;
    
    const group = [index];
    processed.add(index);
    
    // Finde alle Badges, die mit dem aktuellen Badge überlappen
    flags.forEach((otherFlag, otherIndex) => {
      if (otherIndex === index || processed.has(otherIndex)) return;
      
      const distance = Math.abs(flag.position - otherFlag.position);
      if (distance <= threshold) {
        group.push(otherIndex);
        processed.add(otherIndex);
      }
    });
    
    if (group.length > 1) {
      overlappingGroups.push(group);
    }
  });
  
  return overlappingGroups;
};

/**
 * Berechnet die Position für Badges in einem Kreis um einen Mittelpunkt
 */
const calculateCirclePositions = (centerX, centerY, radius, count, startAngle = 0) => {
  const positions = [];
  const angleStep = (2 * Math.PI) / count;
  
  for (let i = 0; i < count; i++) {
    const angle = startAngle + i * angleStep;
    const x = centerX + radius * Math.cos(angle);
    const y = centerY + radius * Math.sin(angle);
    positions.push({ x, y });
  }
  
  return positions;
};

/**
 * Rendert echte Moodle Badge Flaggen für einen Studenten
 */
const MoodleBadgeFlags = ({ 
  student, 
  taskData, 
  containerWidth, 
  rowHeight = 30,
  onFlagClick = null,
  userBadgeData = [],
  showOutsideBadges = true // Neuer Parameter um außerhalb-Badges zu steuern
}) => {
  const [clickedGroup, setClickedGroup] = useState(null);
  const [clickPosition, setClickPosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  // Handle badge click für Fan-Out Effekt
  const handleBadgeClick = useCallback((e, groupIndex, flag) => {
    e.stopPropagation();
    
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const clickPos = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      };
      
      if (clickedGroup === groupIndex) {
        // Wenn die gleiche Gruppe bereits aktiv ist, schließe sie
        setClickedGroup(null);
      } else {
        // Aktiviere neue Gruppe
        setClickedGroup(groupIndex);
        setClickPosition(clickPos);
      }
    }
    
    // Rufe die ursprüngliche onFlagClick nur auf, wenn keine Gruppe aktiv wird
    if (onFlagClick && (clickedGroup === groupIndex || groupIndex === -1)) {
      onFlagClick(flag);
    }
  }, [clickedGroup, onFlagClick]);
  
  // Handle container click um Fan-Out zu schließen
  const handleContainerClick = useCallback((e) => {
    // Schließe Fan-Out wenn irgendwo im Container geklickt wird
    if (clickedGroup !== null) {
      setClickedGroup(null);
    }
  }, [clickedGroup]);
  
  // Globaler Event-Listener um Fan-Out bei Klicks außerhalb zu schließen
  React.useEffect(() => {
    const handleGlobalClick = (e) => {
      if (clickedGroup !== null && containerRef.current && !containerRef.current.contains(e.target)) {
        setClickedGroup(null);
      }
    };
    
    if (clickedGroup !== null) {
      document.addEventListener('click', handleGlobalClick);
      return () => document.removeEventListener('click', handleGlobalClick);
    }
  }, [clickedGroup]);

  if (!taskData.linkEvidence || taskData.evidenceType !== 'moodle-badge' || !taskData.evidenceData?.badges) {
    return null;
  }
  
  const flags = calculateRealFlagPositions(
    userBadgeData, 
    student, 
    taskData?.evidenceData?.badges?.map(b => b.id) || [], 
    taskData?.startDate, 
    taskData?.endDate,
    taskData,
    containerWidth // Übergabe der echten Container-Breite (wird aber nicht mehr verwendet)
  );
  
  // Stelle sicher, dass die Task-Daten Date-Objekte sind für Vergleiche
  const taskStartDate = taskData.startDate instanceof Date ? taskData.startDate : new Date(taskData.startDate);
  const taskEndDate = taskData.endDate instanceof Date ? taskData.endDate : new Date(taskData.endDate);
  
  // Finde überlappende Badge-Gruppen
  const overlappingGroups = findOverlappingBadges(flags, 25);
  
  return (
    <div 
      ref={containerRef}
      style={{ position: 'relative', width: '100%', height: '100%' }}
      onClick={handleContainerClick}
    >
      {flags.map((flag, index) => {
        const flagX = flag.position; // Absolute Position in Pixeln
        
        // Prüfe, ob das Badge nach dem Task-Ende liegt (zu spät)
        const isLate = flag.date > taskEndDate;
        
        // Prüfe, ob das Badge vor dem Task-Start liegt (zu früh)
        const isEarly = flag.date < taskStartDate;
        
        const isOutsideTaskPeriod = !flag.isWithinTaskPeriod;
        
        // Definiere isOutside hier, bevor es verwendet wird
        const isOutside = isOutsideTaskPeriod;
        
        // Wenn showOutsideBadges false ist, zeige nur Badges innerhalb des Task-Zeitraums
        if (!showOutsideBadges && isOutsideTaskPeriod) {
          return null;
        }
        
        // Prüfe, ob dieses Badge zu einer überlappenden Gruppe gehört
        const overlappingGroupIndex = overlappingGroups.findIndex(group => group.includes(index));
        const isInOverlappingGroup = overlappingGroupIndex !== -1;
        const isGroupClicked = clickedGroup === overlappingGroupIndex;
        

        
        // Berechne Position basierend auf Überlappung und Click-Status
        let displayX = flagX;
        let displayY = rowHeight / 2 + (isOutside ? 10 : 15) + 1;
        
        if (isInOverlappingGroup && isGroupClicked) {
          // Verwende Fan-Out Position wenn die Gruppe geklickt wurde
          const group = overlappingGroups[overlappingGroupIndex];
          const positionInGroup = group.indexOf(index);
          const radius = Math.max(15, group.length * 4); // Dynamischer Radius basierend auf Gruppengröße
          
          const circlePositions = calculateCirclePositions(
            clickPosition.x, 
            clickPosition.y, 
            radius, 
            group.length,
            -Math.PI / 2 // Start oben
          );
          
          displayX = circlePositions[positionInGroup].x;
          displayY = circlePositions[positionInGroup].y;
        }        // Dynamisches Styling basierend auf Badge-Position
        
        let badgeStyle = {
          backgroundColor: flag.color,
          border: isOutside 
            ? `1px solid ${isEarly ? '#ffc107' : isLate ? '#dc3545' : flag.color}` 
            : '2px solid white',
          boxShadow: isOutside
            ? isEarly 
              ? '0 2px 6px rgba(0,0,0,0.3), 0 0 2px 2px rgba(255, 193, 7, 0.8)' 
              : isLate 
              ? '0 2px 6px rgba(0,0,0,0.3), 0 0 2px 2px rgba(220, 53, 69, 0.8)'
              : '0 2px 6px rgba(0,0,0,0.3)'
            : isLate
            ? '0 2px 6px rgba(0,0,0,0.3), 0 0 8px 2px rgba(220, 53, 69, 0.6)'
            : isEarly
            ? '0 2px 6px rgba(0,0,0,0.3), 0 0 8px 2px rgba(255, 193, 7, 0.6)'
            : '0 2px 6px rgba(0,0,0,0.3)'
        };
        
        // Badge-Counter für überlappende Gruppen (nur für das erste Badge der Gruppe anzeigen)
        const badgeCount = isInOverlappingGroup ? overlappingGroups[overlappingGroupIndex].length : 1;
        const isFirstInGroup = isInOverlappingGroup ? overlappingGroups[overlappingGroupIndex][0] === index : false;
        const showCounter = badgeCount > 1 && !isGroupClicked && isFirstInGroup;
        
        // Bestimme Icon basierend auf Position
        const badgeIcon = isOutside 
          ? (isEarly ? '⏰' : isLate ? '⚠️' : '🏅')
          : '🏅';
        
        // Bestimme Größe und z-Index
        const badgeSize = isOutside ? '20px' : '18px';
        const baseScale = isOutside ? 1.1 : 1.0;
        let zIndex = isOutside ? 2000 : 1100;
        
        // Erhöhe z-Index für geklickte Gruppen
        if (isInOverlappingGroup && isGroupClicked) {
          zIndex += 1000;
        }
        
        // Erhöhe z-Index für Badges mit Counter, damit der Counter sichtbar bleibt
        if (showCounter) {
          zIndex += 500; // Badge mit Counter liegt über anderen Badges
        }

        return (
          <div
            key={flag.id}
            style={{
              position: 'absolute',
              left: isInOverlappingGroup && isGroupClicked ? `${displayX}px` : `${flagX}px`,
              top: `${displayY}px`,
              transform: isInOverlappingGroup && isGroupClicked 
                ? `translate(-50%, -50%) scale(${baseScale * 1.3})` 
                : `translate(-50%, -50%) scale(${baseScale})`,
              width: badgeSize,
              height: badgeSize,
              ...badgeStyle,
              borderRadius: '50%',
              cursor: 'pointer',
              zIndex: zIndex,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: isOutside ? '11px' : '10px',
              color: 'white',
              fontWeight: 'bold',
              transition: isInOverlappingGroup ? 'all 0.3s ease-out' : 'transform 0.2s ease',
              animation: isOutside && (isEarly || isLate) ? 'pulse 2s infinite' : 'none'
            }}
            onClick={(e) => {
              if (isInOverlappingGroup) {
                // Für überlappende Badges: Toggle Fan-Out
                handleBadgeClick(e, overlappingGroupIndex, flag);
              } else {
                // Für einzelne Badges: schließe Fan-Out falls offen und führe normale Klick-Funktion aus
                e.stopPropagation();
                
                // Schließe Fan-Out falls aktiv
                if (clickedGroup !== null) {
                  setClickedGroup(null);
                }
                
                // Normale Klick-Funktion
                if (onFlagClick) onFlagClick(flag);
              }
            }}
            onMouseEnter={(e) => {
              if (!isInOverlappingGroup) {
                e.target.style.transform = `translate(-50%, -50%) scale(${baseScale * 1.2})`;
              }
            }}
            onMouseLeave={(e) => {
              if (!isInOverlappingGroup) {
                e.target.style.transform = `translate(-50%, -50%) scale(${baseScale})`;
              }
            }}
            title={`${flag.badgeName}\nDatum: ${flag.date.toLocaleDateString('de-DE')}\nStudent: ${student.vorname} ${student.nachname}${isOutside ? '\n(Außerhalb Task-Zeitraum)' : ''}${isInOverlappingGroup ? `\n(${badgeCount} Badges - Klick für Fan-Out)` : ''}`}
          >
            {badgeIcon}
            
            {/* Badge-Counter für mehrere überlappende Badges */}
            {showCounter && (
              <div
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  backgroundColor: '#aac4ff',
                  color: 'black',
                  borderRadius: '50%',
                  width: '14px',
                  height: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '8px',
                  fontWeight: 'bold',
                  border: '1px solid white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.5)',
                  zIndex: zIndex + 100, // Counter liegt über dem Badge
                  pointerEvents: 'none' // Verhindert, dass der Counter Klicks abfängt
                }}
              >
                {badgeCount}
              </div>
            )}
          </div>
        );
      })}
      
      {/* CSS Animation für pulse-Effekt wird über globales CSS definiert */}
    </div>
  );
};

export default MoodleBadgeFlags;

/**
 * Hook für echte Moodle Badge Flag-Logik
 */
export const useMoodleBadgeFlags = (taskData, students) => {
  const [flagsData, setFlagsData] = React.useState({});
  const [userBadgeData, setUserBadgeData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);
  
  // Stabile Dependencies mit useMemo
  const stableBadgeIds = React.useMemo(() => {
    return taskData?.evidenceData?.badges ? JSON.stringify(taskData.evidenceData.badges.map(badge => badge.id).sort()) : null;
  }, [taskData?.evidenceData?.badges]);
  
  const stableStudentIds = React.useMemo(() => {
    return students ? students.map(s => s.id).sort().join(',') : '';
  }, [students]);
  
  const stableTaskDates = React.useMemo(() => {
    const startTime = taskData?.startDate ? new Date(taskData.startDate).getTime() : null;
    const endTime = taskData?.endDate ? new Date(taskData.endDate).getTime() : null;
    return `${startTime}-${endTime}`;
  }, [taskData?.startDate, taskData?.endDate]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (!taskData?.linkEvidence || taskData.evidenceType !== 'moodle-badge' || !taskData.evidenceData?.badges) {
      setFlagsData({});
      setUserBadgeData([]);
      return;
    }

    // Prüfe ob Studenten vorhanden sind
    if (!students || students.length === 0) {
      setFlagsData({});
      setUserBadgeData([]);
      setLoading(false);
      return;
    }
    
    const loadUserBadgeData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Extrahiere Badge-IDs aus den Task-Daten
        const badgeIds = taskData.evidenceData.badges.map(badge => badge.id);
        
        // Lade Badge-Zuweisungen von der API
        const response = await fetchUserBadgeAwards(badgeIds);
        
        if (response.error) {
          throw new Error(response.error);
        }

        const userData = response.userBadges || [];
        setUserBadgeData(userData);
        
        // Berechne Flag-Daten für jeden Studenten
        const newFlagsData = {};
        students.forEach(student => {
          newFlagsData[student.id] = calculateRealFlagPositions(
            userData,
            student,
            badgeIds,
            taskData.startDate,
            taskData.endDate,
            taskData
          );
        });
        
        setFlagsData(newFlagsData);
      } catch (error) {
        setError(error.message);
        setFlagsData({});
        setUserBadgeData([]);
      } finally {
        setLoading(false);
      }
    };
    
    loadUserBadgeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    taskData?.linkEvidence,
    taskData?.evidenceType,
    stableBadgeIds,
    stableTaskDates,
    stableStudentIds
  ]); // Nur stabile Dependencies verwenden
  
  const refreshFlags = async () => {
    // Erzwinge Neuladen der Daten
    if (taskData?.linkEvidence && taskData.evidenceType === 'moodle-badge' && taskData.evidenceData?.badges && students && students.length > 0) {
      setLoading(true);
      setError(null);
      
      try {
        const badgeIds = taskData.evidenceData.badges.map(badge => badge.id);
        const response = await fetchUserBadgeAwards(badgeIds);
        
        if (response.error) {
          throw new Error(response.error);
        }
        
        const userData = response.userBadges || [];
        setUserBadgeData(userData);
        
        const newFlagsData = {};
        students.forEach(student => {
          newFlagsData[student.id] = calculateRealFlagPositions(
            userData,
            student,
            badgeIds,
            taskData.startDate,
            taskData.endDate,
            taskData
          );
        });
        
        setFlagsData(newFlagsData);
        
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    }
  };
  
  return {
    flagsData,
    userBadgeData,
    loading,
    error,
    refreshFlags,
    getFlagsForStudent: (studentId) => flagsData[studentId] || []
  };
};